import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
          <div className="text-center px-4">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">该文章可能已被删除或链接错误</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const remaining = 3 - relatedArticles.length;
    const extra = articles.filter((a) => a.id !== article.id && a.categorySlug !== article.categorySlug).slice(0, remaining);
    relatedArticles.push(...extra);
  }

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},51网址导航,网红塌房,黑料,吃瓜`}
        pageType="article"
        ogImage={article.imageUrl}
        articleData={{
          publishDate: article.publishDate,
          category: article.category,
          tags: article.tags,
        }}
      />

      <Navbar />

      <main>
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-20 pb-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <span className="text-foreground-900 font-medium truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>

        {/* Article Content */}
        <article className="pb-12 md:pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left - Image */}
              <div className="lg:w-2/5 shrink-0">
                <div className="sticky top-20">
                  <span className="inline-block px-3 py-1 border border-foreground-300 text-foreground-600 text-xs font-medium rounded-full mb-4 whitespace-nowrap">
                    {article.category}
                  </span>
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={`${article.title} - 51网址导航`}
                      className="w-full aspect-[4/3] object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="lg:w-3/5">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-foreground-500 text-xs mb-4">
                  <span><i className="ri-eye-line mr-1"></i>{article.views}</span>
                  <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                  <span><i className="ri-time-line mr-1"></i>阅读时长 {article.duration}</span>
                </div>

                <p className="text-foreground-600 text-sm leading-relaxed mb-6 p-4 bg-background-100 rounded-xl">
                  {article.excerpt}
                </p>

                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
                  <span className="text-xs text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-900 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-4 mt-6">
                  <button className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-play-circle-line text-base w-5 h-5 flex items-center justify-center"></i>
                    观看视频
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 border border-foreground-300 text-foreground-700 text-sm rounded-full hover:bg-background-100 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line text-base w-5 h-5 flex items-center justify-center"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading text-foreground-900 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((related, index) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group bg-background-50 rounded-2xl p-5 flex flex-col transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden mb-4">
                    <img
                      src={related.imageUrl}
                      alt={related.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 ${
                      index % 3 === 0 ? 'bg-primary-500/10' : index % 3 === 1 ? 'bg-accent-500/10' : 'bg-secondary-500/10'
                    }`}></div>
                  </div>
                  <span className="text-xs text-primary-500 mb-2 whitespace-nowrap">{related.category}</span>
                  <h3 className="text-sm font-heading text-foreground-900 mb-3 line-clamp-2">
                    {related.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span>{related.views} 阅读</span>
                    <span>{related.publishDate}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}